import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, ChevronRight, Check, X, CalendarClock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ESITI = [["risposto", "Risposto"], ["non_risponde", "Non risponde"], ["numero_errato", "Numero errato"], ["non_contattare", "Non contattare"]];
const btn = "px-2.5 py-1 rounded-lg border border-[var(--border)] bg-white text-xs font-semibold hover:border-[#FC82ED]/50 transition-colors";

function Azioni({ r, onDone }) {
  const { isSegreteria, isDirezione } = useAuth();
  const [rimanda, setRimanda] = useState(false);
  const [data, setData] = useState("");
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); onDone(r); } catch (e) { toast.error(apiError(e)); } };
  const nota = (t) => { const v = window.prompt(t); return v === null ? null : v.trim(); };
  if (r.voce === "chiamate" && (isSegreteria || !isDirezione)) return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {ESITI.map(([e, l]) => (
        <button key={e} data-testid={`elenco-chiamata-${r.id}-${e}`} className={btn} onClick={() => { const n = nota(`${l} — nota (facoltativa):`); if (n !== null) run(() => api.post(`/chiamate/${r.entita}/${r.id}/completa`, { esito: e, nota: n, origine: "centrale" }), `Esito registrato: ${l}`); }}>{l}</button>
      ))}
    </div>
  );
  if (r.voce === "task") return (
    <div className="flex flex-wrap items-center gap-1.5 mt-2">
      <button data-testid={`elenco-task-fatto-${r.id}`} className={btn} onClick={() => { const n = nota("Fatto — nota (facoltativa):"); if (n !== null) run(() => api.put(`/tasks/${r.id}`, { stato: "completato", ...(n ? { note: n } : {}) }), "Task completato"); }}><Check size={12} className="inline mr-1" />Fatto</button>
      {!rimanda ? <button data-testid={`elenco-task-rimanda-${r.id}`} className={btn} onClick={() => setRimanda(true)}><CalendarClock size={12} className="inline mr-1" />Rimanda</button> : <>
        <input type="date" data-testid={`elenco-task-data-${r.id}`} value={data} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setData(e.target.value)} className="px-2 py-1 rounded-lg border border-[var(--border)] text-xs" />
        <button data-testid={`elenco-task-rimanda-ok-${r.id}`} disabled={!data} className={`${btn} disabled:opacity-40`} onClick={() => run(() => api.put(`/tasks/${r.id}`, { due_date: data }), `Rimandato al ${data.split("-").reverse().join("/")}`)}>Conferma</button>
      </>}
    </div>
  );
  if (r.voce === "documenti" && isSegreteria) return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      <button data-testid={`elenco-doc-approva-${r.id}-${r.doc}`} className={btn} onClick={() => run(() => api.post(`/immobili/${r.id}/checklist/approva`, { doc: r.doc }), "Documento approvato")}><Check size={12} className="inline mr-1" />Approva</button>
      <button data-testid={`elenco-doc-rifiuta-${r.id}-${r.doc}`} className={btn} onClick={() => { const m = nota("Motivo del rifiuto (obbligatorio):"); if (m === null) return; if (!m) { toast.error("Scrivi il motivo del rifiuto."); return; } run(() => api.post(`/immobili/${r.id}/checklist/rifiuta`, { doc: r.doc, motivo: m }), "Documento rifiutato"); }}><X size={12} className="inline mr-1" />Rifiuta</button>
    </div>
  );
  return null;
}

export const VOCI = {
  oggi: "Da fare oggi", appuntamenti: "Appuntamenti", chiamate: "Chiamate", documenti: "Doc. da verificare",
  scadenze: "Scadenze (30gg)", ferme: "Pratiche ferme", ticket: "Ticket", task: "Task di agenti e team",
};

function Gruppo({ voce, righe, nav, onDone }) {
  return (
    <div className="mb-5" data-testid={`elenco-gruppo-${voce}`}>
      <div className="text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-2">{VOCI[voce]} · {righe.length}</div>
      <div className="card-immo p-0 divide-y divide-[var(--border)]">
        {righe.map((r) => (
          <div key={`${r.voce}-${r.id}-${r.doc || ""}`} data-testid={`elenco-riga-${r.voce}-${r.id}${r.doc ? `-${r.doc}` : ""}`} className="p-3 hover:bg-slate-50 transition-colors">
          <button onClick={() => nav(r.link)} className="w-full text-left flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">{r.titolo}</div>
              <div className="text-xs text-[var(--muted)] truncate">{r.tipo} · {r.info} · Agente: <b>{r.agente}</b></div>
            </div>
            <ChevronRight size={16} className="text-[var(--muted)] shrink-0" />
          </button>
          <Azioni r={r} onDone={onDone} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CentraleElenco() {
  const [sp, setSp] = useSearchParams();
  const nav = useNavigate();
  const voce = sp.get("voce") || "oggi";
  const all = sp.get("all") === "1";
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get("/dashboard/centrale/elenco", { params: { voce, all: all ? 1 : 0 } }).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e)));
  }, [voce, all]);
  const gruppi = (d?.righe || []).reduce((g, r) => { (g[r.voce] = g[r.voce] || []).push(r); return g; }, {});
  const togli = (x) => setD((p) => { const righe = p.righe.filter((r) => !(r.voce === x.voce && r.id === x.id && (r.doc || "") === (x.doc || ""))); return { ...p, righe, totale: righe.length }; });
  const setAll = (v) => setSp({ voce, all: v ? "1" : "0" });
  return (
    <div className="max-w-4xl mx-auto px-4 py-6 fade-up" data-testid="centrale-elenco">
      <button data-testid="elenco-indietro" onClick={() => nav("/oggi")} className="inline-flex items-center gap-1 text-sm text-[var(--muted)] mb-3 hover:text-[#111]"><ArrowLeft size={15} /> Centrale operativa</button>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <h1 className="font-display text-xl font-extrabold" data-testid="elenco-titolo">
          {VOCI[voce]} — {all ? "tutti" : "i miei team"} — {d ? `${d.totale} ${d.totale === 1 ? "voce" : "voci"}` : "…"}
        </h1>
        <div className="flex items-center gap-1 bg-[#F7F7F8] rounded-full p-1 border border-[var(--border)]">
          <button data-testid="elenco-filter-miei" onClick={() => setAll(false)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${!all ? "bg-[#FC82ED] text-black" : "text-slate-600"}`}>I miei team</button>
          <button data-testid="elenco-filter-tutti" onClick={() => setAll(true)} className={`px-3 py-1.5 rounded-full text-xs font-semibold ${all ? "bg-[#FC82ED] text-black" : "text-slate-600"}`}>Tutti</button>
        </div>
      </div>
      {!d ? <div className="skeleton h-40 rounded-2xl" /> : d.totale === 0 ? <div className="card-immo p-10 text-center text-sm text-[var(--muted)]" data-testid="elenco-vuoto">Nessuna voce.</div>
        : Object.entries(gruppi).map(([v, righe]) => <Gruppo key={v} voce={v} righe={righe} nav={nav} onDone={togli} />)}
    </div>
  );
}
